// src/contexts/ThemeContext.js
// React context for theme management
// All comments in English only

import React, { createContext, useContext, useState, useEffect } from 'react';
import { lightTheme, darkTheme } from '../theme';

const ThemeContext = createContext();

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeModeProvider');
  }
  return context;
};

export const ThemeModeProvider = ({ children }) => {
  // Initialize theme mode from localStorage or default to light
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('themeMode');
    return savedMode === 'dark';
  });

  // Update localStorage when theme mode changes
  useEffect(() => {
    localStorage.setItem('themeMode', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  // Toggle theme mode
  const toggleTheme = () => {
    setIsDarkMode(prev => !prev);
  };

  // Set specific theme mode
  const setThemeMode = (mode) => {
    setIsDarkMode(mode === 'dark');
  };

  // Get current theme object
  const getCurrentTheme = () => {
    return isDarkMode ? darkTheme : lightTheme;
  };

  const value = {
    isDarkMode,
    toggleTheme,
    setThemeMode,
    getCurrentTheme,
    themeMode: isDarkMode ? 'dark' : 'light',
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext; 